import React, { useState, useEffect } from "react";
import {
  Button,
  Checkbox,
  Form,
  Message,
  Modal,
  Loader,
  Label,
  Segment,
  Statistic,
} from "semantic-ui-react";
import { apirequest } from "../../helpers/api-client";
import { t } from "../../constant/translations";
import dayjs from "dayjs";

const TONE_MESSAGES = {
  remind:
    "Уважаеми клиенти, напомняме Ви за непогасени задължения към управляваните от нас имоти. Моля, погасете ги възможно най-скоро.",
  warn:
    "Уважаеми клиенти, имате неплатени задължения към управляваните от нас имоти. Моля, погасете ги в най-скоро време, за да избегнете допълнителни мерки.",
  final_warning:
    "ВАЖНО! Уважаеми клиенти, обръщаме Ви внимание, че имате значителни неплатени задължения към управляваните от нас имоти, които следва да погасите незабавно. При отказ за заплащане може да започнат предвидените от закона процедури за принудително изпълнение.",
};

export const SendRemindersModal = ({ estate }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [formData, setFormData] = useState({
    send_email: true,
    send_text: true,
    tone: "remind",
  });

  useEffect(() => {
    if (modalOpen) {
      setLoadingHistory(true);
      apirequest("GET", `/api/notification-schedules/?estate=${estate.id}`)
        .then((response) => {
          const data = response.body || response;
          const history = Array.isArray(data) ? data : data.results || [];
          setNotificationHistory(history.slice(0, 6));
          setLoadingHistory(false);
        })
        .catch((error) => {
          console.error("Error loading notification history:", error);
          setNotificationHistory([]);
          setLoadingHistory(false);
        });
    }
  }, [modalOpen, estate.id]);

  const resetState = () => {
    setLoading(false);
    setError(null);
    setSuccess(false);
    setFormData({
      send_email: true,
      send_text: true,
      tone: "remind",
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    setLoading(true);
    setError(null);
    setSuccess(false);

    apirequest("POST", `/api/estate/${estate.id}/send_reminders/`, formData)
      .then((response) => {
        setLoading(false);
        setSuccess(true);
        setError(null);

        apirequest("GET", `/api/notification-schedules/?estate=${estate.id}`)
          .then((historyResponse) => {
            const data = historyResponse.body || historyResponse;
            const history = Array.isArray(data) ? data : data.results || [];
            setNotificationHistory(history.slice(0, 6));
          })
          .catch((error) => {
            console.error("Error reloading notification history:", error);
          });

        setTimeout(() => {
          setModalOpen(false);
          resetState();
        }, 2000);
      })
      .catch((error) => {
        setLoading(false);
        setError(error.message || t("error.savng"));
        setSuccess(false);
      });
  };

  const handleCheckboxChange = (field) => {
    setFormData({
      ...formData,
      [field]: !formData[field],
    });
  };

  const handleToneChange = (e, { value }) => {
    setFormData({
      ...formData,
      tone: value,
    });
  };

  const handleCloseModal = () => {
    if (!loading) {
      setModalOpen(false);
      resetState();
    }
  };

  return (
    <Modal
      onOpen={() => setModalOpen(true)}
      onClose={handleCloseModal}
      open={modalOpen}
      trigger={
        <Button
          basic
          color="blue"
          size="tiny"
          icon="paper plane"
          content={t("estate.send_reminders")}
          title={t("estate.send_reminders")}
        />
      }
      closeIcon={true}
    >
      <Modal.Header>
        {t("estate.send_reminders_to")} {estate.name}
      </Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <p>{t("estate.send_reminders_description")}</p>

          <h4>{t("estate.notification_history")}</h4>
          {loadingHistory ? (
            <Loader active inline="centered" />
          ) : notificationHistory.length === 0 ? (
            <p>
              <em>{t("estate.no_previous_notifications")}</em>
            </p>
          ) : (
            <div
              style={{
                marginBottom: "1.5em",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              {notificationHistory.map((notification) => (
                <Segment
                  key={notification.id}
                  style={{ marginBottom: "8px", padding: "10px 12px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "15px",
                      width: "100%",
                    }}
                  >
                    <div style={{ textAlign: "center", minWidth: "130px" }}>
                      <div
                        style={{
                          fontSize: "1.1em",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        {dayjs(notification.created_at).format(
                          "YYYY-MM-DD HH:mm"
                        )}
                      </div>
                      {notification.tone && (
                        <div
                          style={{
                            fontSize: "0.75em",
                            fontWeight: "600",
                            color:
                              notification.tone === "remind"
                                ? "#767676"
                                : notification.tone === "warn"
                                ? "#f2711c"
                                : "#db2828",
                          }}
                        >
                          {t(`estate.tone_${notification.tone}`)}
                        </div>
                      )}
                    </div>

                    <div style={{ textAlign: "center", flex: 1 }}>
                      <div
                        style={{
                          fontSize: "0.95em",
                          fontWeight: "600",
                          color:
                            notification.channel === "email"
                              ? "#2185d0"
                              : "#21ba45",
                        }}
                      >
                        {notification.to}
                      </div>
                      <div style={{ color: "#999", fontSize: "0.75em" }}>
                        {notification.channel}
                      </div>
                    </div>

                    {notification.triggered_by_email && (
                      <div style={{ textAlign: "center", flex: 1 }}>
                        <div
                          style={{
                            fontSize: "0.85em",
                            fontWeight: "500",
                            color: "#555",
                          }}
                        >
                          {notification.triggered_by_email}
                        </div>
                        <div style={{ color: "#999", fontSize: "0.75em" }}>
                          {t("estate.notification_triggered_by")}
                        </div>
                      </div>
                    )}

                    {notification.trigger_context && (
                      <>
                        <div style={{ textAlign: "center", minWidth: "60px" }}>
                          <div
                            style={{
                              fontSize: "1.2em",
                              fontWeight: "bold",
                              color: "#333",
                            }}
                          >
                            {notification.trigger_context
                              .unsettled_charges_count || 0}
                          </div>
                          <div
                            style={{
                              color: "#999",
                              fontSize: "0.75em",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t("estate.notification_charges_count")}
                          </div>
                        </div>
                        <div style={{ textAlign: "center", minWidth: "80px" }}>
                          <div
                            style={{
                              fontSize: "1.2em",
                              fontWeight: "bold",
                              color: "#2185d0",
                            }}
                          >
                            €
                            {notification.trigger_context.total_charge_amount ||
                              "0.00"}
                          </div>
                          <div
                            style={{
                              color: "#999",
                              fontSize: "0.75em",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t("estate.notification_total_amount")}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Segment>
              ))}
            </div>
          )}

          <h4>{t("estate.send_new_reminder")}</h4>
          <Form onSubmit={handleSubmit} error={!!error} success={success}>
            <Form.Field>
              <label>{t("estate.notification_tone")}</label>
              <Form.Select
                value={formData.tone}
                onChange={handleToneChange}
                options={[
                  {
                    key: "remind",
                    value: "remind",
                    text: t("estate.tone_remind"),
                  },
                  { key: "warn", value: "warn", text: t("estate.tone_warn") },
                  {
                    key: "final_warning",
                    value: "final_warning",
                    text: t("estate.tone_final_warning"),
                  },
                ]}
              />
            </Form.Field>

            <Message info>
              <p style={{ fontSize: "0.9em", lineHeight: "1.4em" }}>
                {TONE_MESSAGES[formData.tone]}
              </p>
            </Message>

            <Form.Field>
              <Checkbox
                label={t("estate.send_email")}
                checked={formData.send_email}
                onChange={() => handleCheckboxChange("send_email")}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                label={t("estate.send_text")}
                checked={formData.send_text}
                onChange={() => handleCheckboxChange("send_text")}
              />
            </Form.Field>

            {error && <Message error content={error} />}
            {success && (
              <Message success content={t("estate.reminders_scheduled")} />
            )}

            <Button
              type="submit"
              fluid
              size="large"
              positive
              loading={loading}
              disabled={!formData.send_email && !formData.send_text}
            >
              {t("estate.send")}
            </Button>
          </Form>
        </Modal.Description>
      </Modal.Content>
    </Modal>
  );
};
